<template>
  <div class="p-6 max-w-3xl mx-auto" :dir="locale === 'ar' ? 'rtl' : 'ltr'">
    <!-- Filters + Search -->
    <div class="flex flex-wrap items-center gap-3 mb-6">
      <button
        class="px-4 py-1.5 rounded-full text-sm font-medium border transition-colors"
        :class="filter === 'all' ? 'bg-gray-800 text-white border-gray-800' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'"
        @click="filter = 'all'"
      >
        {{ $t('views.notifications.all') }}
      </button>
      <button
        class="px-4 py-1.5 rounded-full text-sm font-medium border transition-colors"
        :class="filter === 'unread' ? 'bg-red-100 text-red-600 border-red-200' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'"
        @click="filter = 'unread'"
      >
        {{ $t('views.notifications.unread') }}
        <span v-if="unreadCount > 0" class="ms-1">({{ unreadCount }})</span>
      </button>
      <button
        class="px-4 py-1.5 rounded-full text-sm font-medium border transition-colors"
        :class="filter === 'read' ? 'bg-green-100 text-green-700 border-green-200' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'"
        @click="filter = 'read'"
      >
        {{ $t('views.notifications.read') }}
      </button>
      <button
        class="px-4 py-1.5 rounded-full text-sm font-medium border transition-colors"
        :class="filter === 'reminder' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'"
        @click="filter = 'reminder'"
      >
        {{ $t('views.notifications.reminders') }}
      </button>

      <input
        v-model="search"
        type="text"
        class="flex-1 min-w-[180px] border border-gray-300 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-blue-400 transition-colors"
        :placeholder="$t('views.notifications.searchPlaceholder')"
      />

      <button
        v-if="unreadCount > 0"
        :disabled="markingAll"
        class="ms-auto px-4 py-1.5 rounded-full text-sm font-medium border border-gray-300 bg-white text-gray-600 hover:border-gray-400 disabled:opacity-50 transition-colors whitespace-nowrap"
        @click="markAllRead"
      >
        {{ $t('views.notifications.markAllRead') }}
      </button>
    </div>

    <!-- Initial Loading -->
    <div v-if="loading" class="flex justify-center py-16">
      <LoadingSpinner diameter="32px" />
    </div>

    <!-- Empty (nothing loaded at all) -->
    <div
      v-else-if="groupedNotifications.length === 0 && allLoaded"
      class="text-center text-gray-400 py-16 text-sm"
    >
      {{ $t('views.notifications.empty') }}
    </div>

    <!-- List -->
    <div v-else class="bg-white rounded-xl border overflow-hidden">
      <template v-for="group in groupedNotifications" :key="group.label">
        <div class="px-4 py-2 bg-gray-50 border-b text-xs font-bold text-gray-500 uppercase tracking-wide">
          {{ group.label }}
        </div>
        <div
          v-if="group.items.length === 0"
          class="px-4 py-3 text-xs text-gray-400 italic border-b last:border-b-0"
        >
          {{ $t('views.notifications.noneForDay') }}
        </div>
        <div
          v-for="item in group.items"
          :key="`${item.entityType}-${item.entityID}`"
          class="flex items-center gap-3 px-4 py-3 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-gray-50"
          :class="item.entityType !== 'reminder' && !item.isRead ? 'bg-red-50 hover:bg-red-50' : ''"
          :style="item.entityType === 'reminder' ? { backgroundColor: '#FEF6C6' } : {}"
          @click="item.entityType !== 'reminder' ? onNotificationClick(item) : openReminderEdit(item)"
        >
          <!-- Unread dot -->
          <div
            class="flex-shrink-0 w-2 h-2 rounded-full transition-colors"
            :class="item.isRead ? 'bg-transparent' : 'bg-red-500'"
          />

          <!-- Type icon -->
          <div
            class="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
            :class="typeIconBg(item.entityType)"
            :style="item.entityType === 'reminder' ? { backgroundColor: '#FECF08' } : {}"
          >
            <Icon v-if="item.entityType === 'reminder'" icon="mdi:clipboard-list" class="text-white text-sm" />
            <font-awesome-icon v-else :icon="typeIcon(item.entityType)" class="text-white text-sm" />
          </div>

          <!-- Text -->
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-1.5">
              <span class="text-sm font-semibold text-[#0B1F3B]">
                {{ item.entityType === 'reminder' && item.title ? item.title : $t(`views.notifications.types.${item.entityType}`) }}
              </span>
              <span
                v-if="item.entityType === 'reminder' && item.repeatType"
                class="text-xs font-medium text-blue-600 capitalize"
              >
                {{ $t(`views.notifications.repeat.${item.repeatType}`) }}
              </span>
            </div>
            <div class="text-xs text-gray-500 truncate">{{ item.subTitle }}</div>
          </div>

          <!-- Time (non-reminder, non-quote, non-birthday, non-order-due) -->
          <div
            v-if="item.entityType !== 'quote' && item.entityType !== 'birthday' && item.entityType !== 'reminder' && item.entityType !== 'order_due'"
            class="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap"
            dir="ltr"
          >
            {{ formatTime(item.date) }}
          </div>

          <!-- Reminder actions: time + Delete (trash) + Done -->
          <template v-if="item.entityType === 'reminder'">
            <div class="flex-shrink-0 text-xs text-gray-400 whitespace-nowrap" dir="ltr">
              {{ formatTime(item.date) }}
            </div>
            <button
              class="flex-shrink-0 flex items-center justify-center w-7 h-7 rounded hover:bg-red-50 text-red-400 hover:text-red-600 transition-colors"
              :title="$t('views.notifications.reminder.delete')"
              @click.stop="onDeleteReminder(item)"
            >
              <Icon icon="mdi:trash-can-outline" class="text-base" />
            </button>
            <button
              class="flex-shrink-0 text-xs font-medium px-2 py-1 rounded bg-gray-100 hover:bg-gray-200 text-gray-700 transition-colors whitespace-nowrap"
              @click.stop="onMarkReminderDone(item)"
            >
              {{ $t('views.notifications.reminder.markDone') }}
            </button>
          </template>
        </div>
      </template>

      <!-- Load-more spinner -->
      <div v-if="loadingMore" class="flex justify-center py-4">
        <LoadingSpinner diameter="24px" />
      </div>

      <!-- End of history -->
      <div v-if="allLoaded && groupedNotifications.length > 0" class="text-center text-gray-400 py-4 text-xs">
        {{ $t('views.notifications.allCaughtUp') }}
      </div>
    </div>

    <!-- Load more button (shown when last fetched day was empty) -->
    <div v-if="showLoadMoreBtn && !allLoaded" class="flex justify-center mt-4">
      <button
        :disabled="loadingMore"
        class="px-5 py-2 rounded-full text-sm font-medium border border-gray-300 bg-white text-gray-600 hover:border-gray-400 disabled:opacity-50 transition-colors"
        @click="loadNextDay"
      >
        {{ $t('views.notifications.loadMore') }}
      </button>
    </div>

    <!-- Scroll sentinel -->
    <div v-if="!showLoadMoreBtn" ref="sentinel" class="h-1" />

    <!-- Edit reminder dialog -->
    <ReminderDialog
      v-model="reminderDialogVisible"
      :reminder="selectedReminder"
      @saved="onReminderChanged"
      @deleted="onReminderChanged"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Icon } from '@iconify/vue'
import { useHTTP } from '@/plugins/http'
import Notification, { type NotificationEntityType } from '@/models/notification/Notification'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ReminderDialog from '@/views/notifications/ReminderDialog.vue'
import { formatPostgresDate } from '@/utils/date/index'
import dayjs from 'dayjs/esm/index.js'

const MAX_LOOKBACK_DAYS = 90
const DAYS_PER_PAGE     = 3

export default defineComponent({
  name: 'NotificationsPage',
  components: { LoadingSpinner, Icon, ReminderDialog },

  setup() {
    const { t, locale } = useI18n()
    const http          = useHTTP()
    const router        = useRouter()

    const loading         = ref(false)
    const loadingMore     = ref(false)
    const markingAll      = ref(false)
    const allLoaded       = ref(false)
    const showLoadMoreBtn = ref(false)
    const notifications   = ref<Notification[]>([])
    const emptyDates      = ref<string[]>([])
    const filter          = ref<'all' | 'unread' | 'read' | 'reminder'>('all')
    const search          = ref('')

    const reminderDialogVisible = ref(false)
    const selectedReminder      = ref<Notification | null>(null)

    const cursorDate       = ref(dayjs().format('YYYY-MM-DD'))
    const oldestLoadedDate = ref(dayjs().format('YYYY-MM-DD'))
    const sentinel = ref<HTMLElement | null>(null)
    let observer: IntersectionObserver | null = null

    const unreadCount = computed(() => notifications.value.filter(n => !n.isRead).length)

    const filteredNotifications = computed(() => {
      let list = notifications.value

      if (filter.value === 'unread') list = list.filter(n => !n.isRead)
      else if (filter.value === 'read') list = list.filter(n => n.isRead)
      else if (filter.value === 'reminder') list = list.filter(n => n.entityType === 'reminder')

      if (search.value.trim()) {
        const q = search.value.trim().toLowerCase()
        list = list.filter(n =>
          t(`views.notifications.types.${n.entityType}`).toLowerCase().includes(q) ||
          n.subTitle.toLowerCase().includes(q)
        )
      }

      return [...list].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    })

    const groupedNotifications = computed(() => {
      const map   = new Map<string, { label: string; items: Notification[] }>()
      const today = dayjs().format('YYYY-MM-DD')

      const makeLabel = (day: string) =>
        day === today
          ? (locale.value === 'ar' ? 'اليوم' : 'TODAY')
          : dayjs(day).format('DD/MM/YYYY')

      for (const n of filteredNotifications.value) {
        const day = dayjs(n.date).format('YYYY-MM-DD')
        if (!map.has(day)) map.set(day, { label: makeLabel(day), items: [] })
        map.get(day)!.items.push(n)
      }

      if (filter.value === 'all') {
        for (const day of emptyDates.value) {
          if (!map.has(day)) map.set(day, { label: makeLabel(day), items: [] })
        }
      }

      return [...map.entries()]
        .sort(([a], [b]) => (a > b ? -1 : 1))
        .map(([, group]) => group)
    })

    const fetchInitial = async () => {
      loading.value = true
      try {
        const data = await http.get<Notification[]>(Notification.ENDPOINT_TODAY)
        const items = (data ?? []).map(Notification.from)
        notifications.value = items
        oldestLoadedDate.value = cursorDate.value
        cursorDate.value = dayjs(cursorDate.value).subtract(1, 'day').format('YYYY-MM-DD')
      } catch {
        // fail silently
      } finally {
        loading.value = false
      }
    }

    const loadNextDay = async () => {
      if (allLoaded.value || loadingMore.value || loading.value) return

      if (dayjs().diff(dayjs(cursorDate.value), 'day') > MAX_LOOKBACK_DAYS) {
        allLoaded.value = true
        return
      }

      showLoadMoreBtn.value = false
      loadingMore.value     = true
      try {
        const datesToFetch = Array.from({ length: DAYS_PER_PAGE }, (_, i) =>
          dayjs(cursorDate.value).subtract(i, 'day').format('YYYY-MM-DD')
        ).filter(d => dayjs().diff(dayjs(d), 'day') <= MAX_LOOKBACK_DAYS)

        const results = await Promise.all(
          datesToFetch.map(date =>
            http.get<Notification[]>(`${Notification.ENDPOINT_BY_DATE}?date=${date}`)
              .then(data => ({ date, items: (data ?? []).map(Notification.from) }))
              .catch(() => ({ date, items: [] as Notification[] }))
          )
        )

        cursorDate.value = dayjs(cursorDate.value).subtract(datesToFetch.length, 'day').format('YYYY-MM-DD')

        if (dayjs().diff(dayjs(cursorDate.value), 'day') > MAX_LOOKBACK_DAYS) {
          allLoaded.value = true
        }

        let anyHadResults = false
        for (const { date, items } of results) {
          if (items.length > 0) {
            notifications.value.push(...items)
            oldestLoadedDate.value = date
            anyHadResults = true
          } else {
            emptyDates.value.push(date)
          }
        }

        if (anyHadResults) {
          if (!allLoaded.value && sentinel.value && observer) {
            observer.unobserve(sentinel.value)
            observer.observe(sentinel.value)
          }
        } else if (!allLoaded.value) {
          showLoadMoreBtn.value = true
        }
      } catch {
        // fail silently
      } finally {
        loadingMore.value = false
      }
    }

    const markAllRead = async () => {
      markingAll.value = true
      try {
        await http.post(Notification.ENDPOINT_MARK_ALL, { fromDate: oldestLoadedDate.value })
        notifications.value.forEach(n => { n.isRead = true })
      } catch {
        // fail silently
      } finally {
        markingAll.value = false
      }
    }

    const onNotificationClick = (item: Notification) => {
      item.isRead = true
      http.post(Notification.ENDPOINT_MARK_READ, {
        entityType: item.entityType,
        entityID:   item.entityID,
      }).catch(() => {})

      if (item.entityType === 'task') {
        router.push({ path: '/hr/tasks', query: { open_task: item.entityID } })
      } else {
        const path = item.navPath()
        if (path) router.push(path)
      }
    }

    const openReminderEdit = (item: Notification) => {
      if (!item.isRead) {
        item.isRead = true
        http.post(Notification.ENDPOINT_MARK_READ, {
          entityType: item.entityType,
          entityID:   item.entityID,
        }).catch(() => {})
      }
      selectedReminder.value = item
      reminderDialogVisible.value = true
    }

    const onMarkReminderDone = async (item: Notification) => {
      try {
        await http.post(`${Notification.ENDPOINT_REMINDERS}/${item.entityID}/done`, {})
        notifications.value = notifications.value.filter(n => n.entityID !== item.entityID)
      } catch {
        // fail silently
      }
    }

    const onDeleteReminder = async (item: Notification) => {
      try {
        await http.delete(`${Notification.ENDPOINT_REMINDERS}/${item.entityID}`)
        notifications.value = notifications.value.filter(n => n.entityID !== item.entityID)
      } catch {
        // fail silently
      }
    }

    const onReminderChanged = () => {
      // Re-fetch today and re-run the existing list
      fetchInitial()
    }

    const typeIcon = (entityType: NotificationEntityType): string => {
      switch (entityType) {
        case 'appointment': return 'calendar-day'
        case 'task':        return 'list'
        case 'birthday':    return 'user'
        case 'quote':       return 'receipt'
        case 'order_due':   return 'file-invoice'
        default:            return 'bell'
      }
    }

    const typeIconBg = (entityType: NotificationEntityType): string => {
      switch (entityType) {
        case 'appointment': return 'bg-blue-500'
        case 'task':        return 'bg-purple-500'
        case 'birthday':    return 'bg-pink-500'
        case 'quote':       return 'bg-green-600'
        case 'order_due':   return 'bg-orange-500'
        case 'reminder':    return ''
        default:            return 'bg-gray-400'
      }
    }

    const formatTime = (dateStr: string): string => {
      if (!dateStr) return ''
      return formatPostgresDate(dateStr, 'hh:mm A')
    }

    onMounted(async () => {
      await fetchInitial()

      if (sentinel.value) {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting && !allLoaded.value) loadNextDay()
          },
          { rootMargin: '200px', threshold: 0 }
        )
        observer.observe(sentinel.value)
      }
    })

    onBeforeUnmount(() => {
      observer?.disconnect()
    })

    return {
      locale,
      loading,
      loadingMore,
      markingAll,
      allLoaded,
      showLoadMoreBtn,
      markAllRead,
      loadNextDay,
      filter,
      search,
      unreadCount,
      groupedNotifications,
      onNotificationClick,
      openReminderEdit,
      onMarkReminderDone,
      onDeleteReminder,
      onReminderChanged,
      reminderDialogVisible,
      selectedReminder,
      typeIcon,
      typeIconBg,
      formatTime,
      sentinel,
    }
  },
})
</script>
